import { Segment } from "semantic-ui-react";

import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { db, firebase } from "astrid-firebase";
import organizationTypes from "astrid-firestore/src/api/organizations/constants/organizationTypes";
import createWork from "astrid-firestore/src/api/works/createWork";
import { Work } from "astrid-firestore/src/api/works/types/Work";
import { WorkMetaData } from "astrid-firestore/src/api/works/types/WorkMetaData";
import updateWork from "astrid-firestore/src/api/works/updateWork";
import getDefaultValues from "astrid-firestore/src/types/getDefaultValues";

import Button from "../../../ui/components/Buttons/Button";
import SidebarPortal from "../../../ui/components/SidebarPortal/SidebarPortal";
import Toast from "../../../ui/components/Toast/Toast";

import useArticleMetadata from "../../../articles/hooks/useArticleMetadata";
import { useProfile } from "../../../authentication/state/profile";
import Form from "../../../forms/components/Form/Form";
import FormFields from "../../../forms/components/FormFields/FormFields";
import FormValidationMessageSticky from "../../../forms/components/FormValidationMessage/FormValidationMessageSticky";
import useSimpleForm from "../../../forms/hooks/useSimpleForm";

import WorkMetaDataBokgruppeSelect from "../WorkMetaDataBokgruppeSelect/WorkMetaDataBokgruppeSelect";

const defaultValues = getDefaultValues(WorkMetaData);

export default function WorkMetaDataEdit({
	work,
	publisher: publisherProp,
	overlay,
	onSuccess: onSuccessProp,
	onClose,
	disableSerie,
}) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const user = useProfile();

	const { form, watch, formState, onSubmit } = useSimpleForm({
		schema: Work,
		defaultValues: {
			...Object.fromEntries(Object.entries(defaultValues).map(([key, value]) => [key, work[key] || value])),
			publisher: publisherProp || null,
		},
		onSubmit: ({ publisher, ...data }) => {
			if (!work.exists) {
				return createWork(firebase, { publisher, createdBy: user, ...data });
			}

			return updateWork(firebase, { ref: work.ref, updatedBy: user, ...data }, { user });
		},
		onSuccess: (result) => {
			if (onSuccessProp) {
				return onSuccessProp(result);
			}

			if (!work.exists) {
				navigate(result.id);
			} else {
				onClose();
			}
		},
	});

	const [publisher, language] = watch(["publisher", "language"]);

	const focus = !work.exists || formState.isDirty;

	const fields = useArticleMetadata({ publisher, disableSerie });

	return (
		<SidebarPortal width={500} focus={focus} open overlay={overlay} onClose={onClose}>
			<Form form={form}>
				<FormValidationMessageSticky />

				<Segment basic padded style={{ margin: 0 }}>
					{!publisherProp && (
						<Form.CollectionSelect
							name="publisher"
							label={t("publisher", "Publisher")}
							query={db.collection("organizations").where("type", "==", organizationTypes.PUBLISHER)}
						/>
					)}

					<FormFields fields={fields} />

					{["no", "nn", "nb"].includes(language) && <WorkMetaDataBokgruppeSelect name="bokgruppe" />}
				</Segment>

				{work.exists && <Form.Blocker />}
			</Form>

			<Toast open>
				<Button onClick={onClose}>{t("cancel", "Cancel")}</Button>

				<Button
					primary
					onClick={onSubmit}
					disabled={formState.isSubmitting || !formState.isDirty}
					loading={formState.isSubmitting}
				>
					{work.exists ? t("saveChanges", "Save Changes") : t("createWork", "Create work")}
				</Button>
			</Toast>
		</SidebarPortal>
	);
}
